import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ChartBar, TrendUp, TrendDown, Warning, Crown, UserMinus } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ClientForecast from '../components/ClientForecast';
import { BACKEND_URL } from '../config';

const AdvancedAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('yoy');
  const [clientList, setClientList] = useState([]);
  const [selectedForecastClient, setSelectedForecastClient] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [analyticsRes, clientsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/analytics/advanced`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/clients?limit=500`, { withCredentials: true }),
      ]);
      setData(analyticsRes.data);
      setClientList(clientsRes.data?.clients || []);
    } catch (err) { console.error(err.message);
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="text-center py-12 text-muted-foreground">Loading advanced analytics...</div>;
  if (!data) return <div className="text-center py-12 text-muted-foreground">No data available</div>;

  const sections = [
    { key: 'yoy', label: 'YoY Growth' },
    { key: 'clv', label: 'Client Value' },
    { key: 'churn', label: 'Churn Risk' },
    { key: 'forecast', label: 'Client Forecast' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <ChartBar size={24} weight="regular" />
        <div>
          <div className="text-xl font-semibold tracking-tight">Advanced Analytics</div>
          <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground">DEEP INSIGHTS</div>
        </div>
      </div>

      {/* Revenue Concentration card */}
      <div className="bg-card border border-border rounded-sm p-5">
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-2">Revenue Concentration</div>
        <div className="flex items-end gap-6 flex-wrap">
          <div>
            <div className="text-3xl font-bold tracking-tight">{data.revenue_concentration.top_10_pct}%</div>
            <div className="text-xs text-muted-foreground mt-1">from top 10 clients</div>
          </div>
          <div className="text-sm text-muted-foreground">
            ₹{data.revenue_concentration.top_10_revenue.toLocaleString()} of ₹{data.revenue_concentration.total_revenue.toLocaleString()} total
          </div>
          <div className="text-sm text-muted-foreground">
            {data.revenue_concentration.total_clients} clients total
          </div>
        </div>
        <div className="w-full h-2 bg-muted rounded-full overflow-hidden mt-3">
          <div className="h-full bg-accent rounded-full" style={{ width: `${data.revenue_concentration.top_10_pct}%` }} />
        </div>
      </div>

      {/* Section tabs */}
      <div className="flex gap-2 flex-wrap">
        {sections.map(s => (
          <button
            key={s.key}
            data-testid={`analytics-tab-${s.key}`}
            onClick={() => setActiveSection(s.key)}
            className={`px-4 py-2 rounded-sm text-sm font-medium transition-colors ${activeSection === s.key ? 'bg-primary text-primary-foreground' : 'bg-card border border-border hover:bg-muted'}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* YoY Growth */}
      {activeSection === 'yoy' && (
        <div className="bg-card border border-border rounded-sm p-5">
          <div className="text-sm font-semibold mb-4">Financial Year Revenue Growth</div>
          {data.yoy_growth.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground text-sm">Need at least 2 financial years of data for FY-over-FY comparison</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={data.yoy_growth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="year" stroke="hsl(var(--foreground))" style={{ fontSize: '12px' }} />
                  <YAxis stroke="hsl(var(--foreground))" style={{ fontSize: '12px' }} tickFormatter={(v) => `₹${(v / 100000).toFixed(0)}L`} />
                  <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '0.25rem' }} />
                  <Bar dataKey="revenue" fill="hsl(var(--accent))" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                {data.yoy_growth.map(y => (
                  <div key={y.year} className="flex items-center gap-3 p-3 border border-border rounded-sm">
                    {y.growth_pct >= 0 ? <TrendUp size={20} weight="bold" className="text-accent" /> : <TrendDown size={20} weight="bold" className="text-destructive" />}
                    <div>
                      <div className="text-sm font-semibold">{y.year}</div>
                      <div className={`text-xs font-bold ${y.growth_pct >= 0 ? 'text-accent' : 'text-destructive'}`}>{y.growth_pct > 0 ? '+' : ''}{y.growth_pct}%</div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* CLV */}
      {activeSection === 'clv' && (
        <div className="bg-card border border-border rounded-sm">
          <div className="p-5 border-b border-border">
            <div className="text-sm font-semibold">Client Lifetime Value (Top 20)</div>
            <div className="text-xs text-muted-foreground mt-0.5">Projected annual value based on historical spending patterns</div>
          </div>
          {data.clv_top_clients.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground text-sm">No client data available</div>
          ) : (
            <div className="divide-y divide-border">
              {data.clv_top_clients.map((c, i) => (
                <div key={c.client_id} data-testid={`clv-row-${c.client_id}`} className="flex items-center gap-4 px-5 py-3 hover:bg-muted/50 transition-colors">
                  <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-bold shrink-0">
                    {i < 3 ? <Crown size={14} weight="fill" className="text-chart-4" /> : i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{c.name}</div>
                    <div className="text-xs text-muted-foreground">{c.months_active} active months · {c.tenure_months}mo tenure</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold">₹{c.projected_annual_value.toLocaleString()}</div>
                    <div className="text-xs text-muted-foreground">₹{c.avg_monthly_revenue.toLocaleString()}/mo</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Churn Risk */}
      {activeSection === 'churn' && (
        <div className="bg-card border border-border rounded-sm">
          <div className="p-5 border-b border-border">
            <div className="text-sm font-semibold flex items-center gap-2">
              <UserMinus size={18} weight="regular" />
              Churn Risk Clients
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">Clients who haven't made purchases in recent months</div>
          </div>
          {data.churn_risk.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground text-sm">
              <div className="mb-1">No at-risk clients detected</div>
              <div className="text-xs">All clients have recent activity</div>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {data.churn_risk.map((c) => {
                const riskColor = c.risk_score >= 80 ? 'text-destructive' : c.risk_score >= 40 ? 'text-chart-4' : 'text-muted-foreground';
                const riskBg = c.risk_score >= 80 ? 'bg-destructive' : c.risk_score >= 40 ? 'bg-chart-4' : 'bg-muted-foreground';
                return (
                  <div key={c.client_id} data-testid={`churn-row-${c.client_id}`} className="flex items-center gap-4 px-5 py-3 hover:bg-muted/50 transition-colors">
                    <div className="shrink-0">
                      <Warning size={18} weight="fill" className={riskColor} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{c.name}</div>
                      <div className="text-xs text-muted-foreground">Last active: {c.last_active_month} · {c.months_inactive} months inactive</div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${riskBg}`} style={{ width: `${c.risk_score}%` }} />
                        </div>
                        <span className={`text-xs font-bold ${riskColor}`}>{c.risk_score}%</span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">₹{c.total_revenue.toLocaleString()}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeSection === 'forecast' && (
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-sm p-4 sm:p-5">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3">Select Client for Forecast</div>
            <Select value={selectedForecastClient} onValueChange={setSelectedForecastClient}>
              <SelectTrigger className="w-full sm:w-[320px] rounded-sm" data-testid="forecast-client-select">
                <SelectValue placeholder="Choose a client..." />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {clientList.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selectedForecastClient && (
            <ClientForecast
              clientId={selectedForecastClient}
              clientName={clientList.find(c => c.id === selectedForecastClient)?.name}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default AdvancedAnalytics;
